import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { AppShell, PageHeader } from "@/components/AppShell";
import {
	Loader2,
	Play,
	Square,
	ArrowLeft,
	Smartphone,
	PanelLeftOpen,
	CheckCircle2,
	AlertCircle,
	Upload,
	X,
	Search,
} from "lucide-react";
import { toast } from 'sonner';
import {
	startGroupAgents,
	stopGroupAgents,
	getGroupRunStatus,
	getSessionDetail,
	previewGroupAgents,
	type RunPreviewEntry,
} from '@/services/vmos-agentic-group-api';
import { fetchDevices, uploadFileToDevices, type VMOSDevice } from '@/services/vmos-api';
import { listBrands } from '@/services/brands-api';
import { listSlackBots, type SlackBot } from '@/services/slack-bots-api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { cn, toAscii } from '@/lib/utils';
import { DeviceCard } from '@/components/vmos/DeviceCard';
import { DeviceDetailSheet } from '@/components/vmos/DeviceDetailSheet';
import type { DeviceStreamState } from '@/components/vmos/shared-types';
import { subscribeToGroupStream, getCachedDeviceStates } from '@/services/group-stream-store';
import { SavedPromptsPanel, type RunFormSnapshot } from '@/components/prompts/SavedPromptsPanel';
import { useTemplate as recordTemplateUse } from '@/services/prompts-api';
import type { Persona } from '@/services/prompts-api';
import type { PromptTemplate } from '@/services/prompts-api';
import { getContentRunStatus, stopContentRun } from '@/services/content-api';

const DEFAULT_LLM_MODEL = "gemini-3-flash-preview";
const DEFAULT_COMMENT_MODEL = "gemini-3-flash-preview";


// === File Upload Panel ===

type FileUploadStatus = 'idle' | 'uploading' | 'done' | 'error';

interface FileEntry {
	id: string;
	file: File;
	autoInstall: boolean;
	status: FileUploadStatus;
	error?: string;
}

interface DeviceFileAssignment {
	files: FileEntry[];
}

const DeviceUploadRow = ({
	padId,
	deviceName,
	assignment,
	disabled,
	onFilesAdd,
	onFileRemove,
	onAutoInstallChange,
}: {
	padId: string;
	deviceName: string;
	assignment: DeviceFileAssignment | undefined;
	disabled: boolean;
	onFilesAdd: (files: File[]) => void;
	onFileRemove: (fileId: string) => void;
	onAutoInstallChange: (fileId: string, v: boolean) => void;
}) => {
	const fileInputRef = useRef<HTMLInputElement>(null);
	const files = assignment?.files ?? [];

	return (
		<div className="space-y-1">
			<div className="flex items-center gap-2 text-xs">
				<div className="flex items-center gap-1.5 w-32 min-w-0">
					<Smartphone className="h-3 w-3 text-muted-foreground shrink-0" />
					<span className="truncate text-muted-foreground font-medium" title={padId}>
						{deviceName}
					</span>
				</div>
				<input
					ref={fileInputRef}
					type="file"
					multiple
					className="hidden"
					onChange={(e) => {
						if (e.target.files && e.target.files.length > 0) {
							onFilesAdd(Array.from(e.target.files));
						}
						e.target.value = '';
					}}
				/>
				<Button
					variant="outline"
					size="sm"
					className="h-7 text-xs"
					disabled={disabled}
					onClick={() => fileInputRef.current?.click()}
				>
					<Upload className="h-3 w-3 mr-1" />
					{files.length > 0 ? `Add More (${files.length})` : 'Choose Files'}
				</Button>
			</div>
			{files.length > 0 && (
				<div className="ml-[8.5rem] space-y-1">
					{files.map((entry) => {
						const isApk = entry.file.name.toLowerCase().endsWith('.apk');
						return (
							<div key={entry.id} className="flex items-center gap-1.5 text-xs">
								{entry.status === 'done' ? (
									<CheckCircle2 className="h-3 w-3 text-green-600 shrink-0" />
								) : entry.status === 'error' ? (
									<span title={entry.error}><AlertCircle className="h-3 w-3 text-destructive shrink-0" /></span>
								) : entry.status === 'uploading' ? (
									<Loader2 className="h-3 w-3 animate-spin text-primary shrink-0" />
								) : null}
								<span className="truncate max-w-[180px]" title={entry.file.name}>
									{entry.file.name.length > 28 ? entry.file.name.slice(0, 25) + '...' : entry.file.name}
								</span>
								{isApk && entry.status !== 'done' && (
									<label className="flex items-center gap-1 text-xs whitespace-nowrap">
										<Checkbox
											checked={entry.autoInstall}
											onCheckedChange={(v) => onAutoInstallChange(entry.id, !!v)}
											disabled={disabled || entry.status === 'uploading'}
										/>
										Auto-install
									</label>
								)}
								{entry.status !== 'done' && entry.status !== 'uploading' && (
									<button
										type="button"
										className="text-muted-foreground hover:text-destructive transition-colors"
										onClick={() => onFileRemove(entry.id)}
										disabled={disabled}
									>
										<X className="h-3 w-3" />
									</button>
								)}
								{entry.status === 'uploading' && (
									<div className="flex-1 h-1 rounded-full bg-muted overflow-hidden ml-1">
										<div className="h-full bg-primary rounded-full animate-upload-bar" />
									</div>
								)}
								{entry.status === 'error' && (
									<span className="text-destructive truncate" title={entry.error}>
										{entry.error || 'Failed'}
									</span>
								)}
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
};

const FileUploadPanel = ({
	syncedPadIds,
	devices,
}: {
	syncedPadIds: string[];
	devices: VMOSDevice[];
}) => {
	const [assignments, setAssignments] = useState<Record<string, DeviceFileAssignment>>({});
	const [destinationPath, setDestinationPath] = useState('/DCIM/');
	const assignmentsRef = useRef(assignments);
	assignmentsRef.current = assignments;
	const destinationRef = useRef(destinationPath);
	destinationRef.current = destinationPath;

	const assignedCount = syncedPadIds.reduce((count, id) => {
		const files = assignments[id]?.files ?? [];
		return count + files.filter(f => f.status !== 'done').length;
	}, 0);

	const addFilesForDevice = useCallback((padId: string, files: File[]) => {
		const newEntries: FileEntry[] = files.map(file => ({
			id: crypto.randomUUID(),
			file,
			autoInstall: false,
			status: 'idle' as const,
		}));
		setAssignments(prev => ({
			...prev,
			[padId]: { files: [...(prev[padId]?.files ?? []), ...newEntries] },
		}));
	}, []);

	const removeFileForDevice = useCallback((padId: string, fileId: string) => {
		setAssignments(prev => ({
			...prev,
			[padId]: { files: (prev[padId]?.files ?? []).filter(f => f.id !== fileId) },
		}));
	}, []);

	const setAutoInstallForDevice = useCallback((padId: string, fileId: string, autoInstall: boolean) => {
		setAssignments(prev => ({
			...prev,
			[padId]: {
				files: (prev[padId]?.files ?? []).map(f =>
					f.id === fileId ? { ...f, autoInstall } : f
				),
			},
		}));
	}, []);

	const uploadMutation = useMutation({
		mutationFn: async () => {
			const current = assignmentsRef.current;
			const dest = destinationRef.current;

			// Collect all (padId, fileEntry) pairs still needing an upload.
			const pending: { padId: string; entry: FileEntry }[] = [];
			for (const padId of syncedPadIds) {
				for (const entry of current[padId]?.files ?? []) {
					if (entry.status !== 'done') {
						pending.push({ padId, entry });
					}
				}
			}
			if (pending.length === 0) return;

			// Group the same file selection across pads by (name, size, lastModified)
			// so each unique file is uploaded ONCE to the backend/R2 and then pushed
			// to every assigned device in a single request. Uploading the same video
			// once per pad in parallel splits the operator's uplink N ways and blows
			// past Railway's 5-minute request-body cap (the 400s in prod logs).
			const byFile = new Map<
				string,
				{ file: File; items: { padId: string; entry: FileEntry }[] }
			>();
			for (const item of pending) {
				const f = item.entry.file;
				const key = `${f.name}|${f.size}|${f.lastModified}`;
				const group = byFile.get(key);
				if (group) {
					group.items.push(item);
				} else {
					byFile.set(key, { file: f, items: [item] });
				}
			}

			const setFileStatuses = (entryIds: Set<string>, status: FileUploadStatus, error?: string) => {
				setAssignments(prev => {
					const next: Record<string, DeviceFileAssignment> = {};
					for (const [padId, assignment] of Object.entries(prev)) {
						const touched = (assignment?.files ?? []).some(f => entryIds.has(f.id));
						if (!touched) {
							next[padId] = assignment;
							continue;
						}
						next[padId] = {
							files: (assignment?.files ?? []).map(f =>
								entryIds.has(f.id)
									? status === 'error'
										? { ...f, status, error }
										: { ...f, status }
									: f
							),
						};
					}
					return next;
				});
			};

			// Sequential per unique file: never split the uplink across concurrent
			// big uploads. autoInstall can differ per pad, so fan out in buckets.
			for (const group of byFile.values()) {
				const byAutoInstall = new Map<boolean, { padId: string; entry: FileEntry }[]>();
				for (const item of group.items) {
					const isApk = item.entry.file.name.toLowerCase().endsWith('.apk');
					const flag = isApk && item.entry.autoInstall;
					const arr = byAutoInstall.get(flag) ?? [];
					arr.push(item);
					byAutoInstall.set(flag, arr);
				}

				for (const [autoInstall, items] of byAutoInstall) {
					const entryIds = new Set(items.map(i => i.entry.id));
					setFileStatuses(entryIds, 'uploading');
					try {
						const result = await uploadFileToDevices({
							file: group.file,
							padIds: items.map(i => i.padId),
							destinationPath: dest,
							autoInstall,
						});
						// Mark done only once the backend accepted the push for every pad.
						const acceptedPadIds = new Set<string>(
							(Array.isArray(result?.pad_ids) ? result.pad_ids : items.map(i => i.padId)) as string[],
						);
						const acceptedIds = new Set(
							items.filter(i => acceptedPadIds.has(i.padId)).map(i => i.entry.id),
						);
						const missing = items.filter(i => !acceptedIds.has(i.entry.id));
						if (acceptedIds.size > 0) setFileStatuses(acceptedIds, 'done');
						if (missing.length > 0) {
							setFileStatuses(new Set(missing.map(i => i.entry.id)), 'error', 'Device did not accept upload');
						}
					} catch (err: any) {
						setFileStatuses(entryIds, 'error', err.message || 'Upload failed');
					}
				}
			}
		},
	});

	const getDeviceName = (padId: string) => {
		const device = devices.find(d => d.pad_id === padId);
		return device?.device_name || padId.slice(0, 8);
	};

	return (
		<div className="space-y-3">
			<div className="flex items-center justify-between">
				<label className="text-sm text-muted-foreground font-medium">Upload files to devices</label>
				<div className="flex items-center gap-2">
					<Input
						placeholder="Dest path"
						value={destinationPath}
						onChange={(e) => setDestinationPath(e.target.value)}
						className="w-28 h-7 text-xs"
					/>
					<Button
						size="sm"
						disabled={assignedCount === 0 || uploadMutation.isPending}
						onClick={() => uploadMutation.mutate()}
					>
						<Upload className="h-3 w-3 mr-1" />
						{uploadMutation.isPending ? `Uploading...` : `Upload ${assignedCount} file${assignedCount !== 1 ? 's' : ''}`}
					</Button>
				</div>
			</div>
			<div className="space-y-2">
				{syncedPadIds.map(padId => (
					<DeviceUploadRow
						key={padId}
						padId={padId}
						deviceName={getDeviceName(padId)}
						assignment={assignments[padId]}
						disabled={uploadMutation.isPending}
						onFilesAdd={(files) => addFilesForDevice(padId, files)}
						onFileRemove={(fileId) => removeFileForDevice(padId, fileId)}
						onAutoInstallChange={(fileId, v) => setAutoInstallForDevice(padId, fileId, v)}
					/>
				))}
			</div>
		</div>
	);
};

// === Setup Mode ===

const SetupMode = ({
	devices,
	selectedIds,
	onToggle,
	task,
	onTaskChange,
	persona,
	onPersonaChange,
	brandId,
	onBrandIdChange,
	maxSteps,
	onMaxStepsChange,
	slackBotId,
	onSlackBotIdChange,
	slackBots,
	disableProactiveSearch,
	onDisableProactiveSearchChange,
	returnHomeOnFinish,
	onReturnHomeOnFinishChange,
	captureRequired,
	onCaptureRequiredChange,
	maxCommentsPerSession,
	onMaxCommentsPerSessionChange,
	maxSearchesPerSession,
	onMaxSearchesPerSessionChange,
	onStart,
	isStarting,
	engagementActive,
}: {
	devices: VMOSDevice[];
	selectedIds: Set<string>;
	onToggle: (padId: string) => void;
	task: string;
	onTaskChange: (task: string) => void;
	persona: string;
	onPersonaChange: (persona: string) => void;
	brandId: string;
	onBrandIdChange: (id: string) => void;
	maxSteps: number;
	onMaxStepsChange: (n: number) => void;
	slackBotId: string;
	onSlackBotIdChange: (id: string) => void;
	slackBots: SlackBot[];
	disableProactiveSearch: boolean;
	onDisableProactiveSearchChange: (v: boolean) => void;
	returnHomeOnFinish: boolean;
	onReturnHomeOnFinishChange: (v: boolean) => void;
	captureRequired: boolean;
	onCaptureRequiredChange: (v: boolean) => void;
	maxCommentsPerSession: number | '';
	onMaxCommentsPerSessionChange: (v: number | '') => void;
	maxSearchesPerSession: number | '';
	onMaxSearchesPerSessionChange: (v: number | '') => void;
	onStart: () => void;
	isStarting: boolean;
	engagementActive: boolean;
}) => {
	const { data: brands = [] } = useQuery({
		queryKey: ['brands'],
		queryFn: listBrands,
		staleTime: 60_000,
	});

	const [deviceSearch, setDeviceSearch] = useState('');

	const filteredDevices = useMemo(() => {
		const q = deviceSearch.toLowerCase();
		if (!q) return devices;
		return devices.filter(
			(d) =>
				(d.device_name?.toLowerCase() || '').includes(q) ||
				(d.pad_id?.toLowerCase() || '').includes(q) ||
				(d.platform?.toLowerCase() || '').includes(q),
		);
	}, [devices, deviceSearch]);

	return (
		<div className="space-y-8 max-w-4xl">
			{/* Devices */}
			<section className="space-y-3">
				<div className="flex items-baseline gap-3">
					<span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
						1 · Devices
					</span>
					{selectedIds.size > 0 && (
						<span className="text-xs font-mono text-muted-foreground">
							{selectedIds.size} of {devices.length} selected
						</span>
					)}
				</div>

				<div className="relative">
					<Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
					<Input
						placeholder="Search devices..."
						value={deviceSearch}
						onChange={(e) => setDeviceSearch(e.target.value)}
						className="pl-8 h-8 text-xs"
					/>
				</div>

				{devices.length === 0 ? (
					<div className="flex flex-col items-center gap-1.5 py-12 text-center border rounded-lg">
						<Smartphone className="h-7 w-7 text-muted-foreground/40" />
						<p className="text-sm font-medium">No devices found</p>
						<p className="text-xs text-muted-foreground">Configure devices first.</p>
					</div>
				) : (
					<>
						{filteredDevices.length === 0 ? (
							<div className="py-8 text-center text-sm text-muted-foreground border rounded-lg rounded-t-none">
								No devices match your search
							</div>
						) : (
							<div className="border rounded-lg divide-y">
								{filteredDevices.map((device) => {
									const selected = selectedIds.has(device.pad_id);
									const isOnline = device.status === 'online';
									return (
										<button
											type="button"
											key={device.pad_id}
											onClick={() => onToggle(device.pad_id)}
											className={cn(
												"flex items-center gap-3 w-full px-4 py-2.5 text-left hover:bg-muted/30 transition-colors",
												selected && "bg-primary/5",
											)}
										>
											<Checkbox
												checked={selected}
												onCheckedChange={() => onToggle(device.pad_id)}
												onClick={(e) => e.stopPropagation()}
											/>
											<Smartphone className="h-4 w-4 text-muted-foreground shrink-0" />
											<span className="font-medium text-sm truncate">{device.device_name}</span>
											{device.username && (
												<span className="text-xs text-muted-foreground font-mono shrink-0">@{device.username}</span>
											)}
											<div className="ml-auto flex items-center gap-3 shrink-0">
												{device.platform && (
													<span className="text-xs text-muted-foreground">{device.platform}</span>
												)}
												{device.status && (
													<span className="inline-flex items-center gap-1.5 text-xs">
														<span className={cn(
															"h-1.5 w-1.5 rounded-full",
															isOnline ? "bg-green-500" : "bg-muted-foreground/40"
														)} />
														<span className={cn(isOnline ? "text-green-600" : "text-muted-foreground")}>
															{device.status}
														</span>
													</span>
												)}
											</div>
										</button>
									);
								})}
							</div>
						)}
					</>
				)}
			</section>

			{devices.length > 0 && (
				<>
					{/* Prompt + persona */}
					<section className="space-y-3">
						<div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
							2 · Prompt
						</div>
						<Textarea
							placeholder="Enter the task for all agents (e.g., 'Like 5 videos and follow 2 creators')"
							value={task}
							onChange={(e) => onTaskChange(e.target.value)}
							className="min-h-[80px]"
						/>
						<Textarea
							placeholder="Agent persona (optional) — describe who these agents are, their interests, how they talk (e.g., '22 year old college student who loves cooking and gym content, types casually with lowercase and occasional emojis')"
							value={persona}
							onChange={(e) => onPersonaChange(e.target.value)}
							className="min-h-[60px]"
						/>
					</section>

					{/* Brand & model */}
					<section className="space-y-3">
						<div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
							3 · Brand &amp; model
						</div>
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
							<div className="space-y-1.5">
								<label className="text-xs text-muted-foreground">Brand (optional)</label>
								<Select value={brandId || 'none'} onValueChange={(v) => onBrandIdChange(v === 'none' ? '' : v)}>
									<SelectTrigger><SelectValue /></SelectTrigger>
									<SelectContent>
										<SelectItem value="none">No brand</SelectItem>
										{brands.map(b => (
											<SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						</div>
						<div className="flex items-center gap-3 pt-1">
							<label className="text-xs text-muted-foreground whitespace-nowrap">
								Max steps <span className="font-mono tabular-nums text-foreground ml-1">{maxSteps}</span>
							</label>
							<input
								type="range"
								min={10}
								max={500}
								step={10}
								value={maxSteps}
								onChange={(e) => onMaxStepsChange(Number(e.target.value))}
								className="flex-1"
							/>
						</div>
						<div className="grid grid-cols-2 gap-2 pt-1">
							<div>
								<label className="text-xs text-muted-foreground block mb-1">Max Comments / Session</label>
								<Input
									type="number"
									min={0}
									placeholder="Auto (8)"
									value={maxCommentsPerSession}
									onChange={(e) =>
										onMaxCommentsPerSessionChange(
											e.target.value === '' ? '' : Math.max(0, Number(e.target.value))
										)
									}
									className="w-full"
								/>
								<p className="text-[11px] text-muted-foreground mt-1">Blank = default (8). 0 = warmup / lurk (no comments).</p>
							</div>
							<div>
								<label className="text-xs text-muted-foreground block mb-1">Max Searches / Session</label>
								<Input
									type="number"
									min={0}
									placeholder="Auto (5)"
									value={maxSearchesPerSession}
									onChange={(e) =>
										onMaxSearchesPerSessionChange(
											e.target.value === '' ? '' : Math.max(0, Number(e.target.value))
										)
									}
									className="w-full"
								/>
								<p className="text-[11px] text-muted-foreground mt-1">Blank = default (5). 0 = warmup / lurk (no searches).</p>
							</div>
						</div>
						<div className="flex items-center gap-2 pt-1">
							<Switch
								id="disable-search"
								checked={disableProactiveSearch}
								onCheckedChange={onDisableProactiveSearchChange}
							/>
							<label htmlFor="disable-search" className="text-xs text-muted-foreground cursor-pointer">
								Feed only — let the persona prompt control search frequency (suppresses system-level trending search nudges)
							</label>
						</div>
						<div className="flex items-center gap-2 pt-1">
							<Switch
								id="capture-required"
								checked={captureRequired}
								onCheckedChange={onCaptureRequiredChange}
							/>
							<label htmlFor="capture-required" className="text-xs text-muted-foreground cursor-pointer">
								Require post-URL capture for FB posts / Reddit — turn off to let the agent comment directly without the copy-link flow
							</label>
						</div>
						<div className="space-y-1.5 pt-1">
							<label className="text-xs text-muted-foreground">When run completes / is stopped</label>
							<Select value={returnHomeOnFinish ? 'home' : 'stay'} onValueChange={(v) => onReturnHomeOnFinishChange(v === 'home')}>
								<SelectTrigger><SelectValue /></SelectTrigger>
								<SelectContent>
									<SelectItem value="home">Return device to home screen</SelectItem>
									<SelectItem value="stay">Leave app open (stay where agent left off)</SelectItem>
								</SelectContent>
							</Select>
						</div>
						{brandId && (
							<p className="text-xs text-muted-foreground">
								Selecting a brand injects its commenting guidelines into the agent's system prompt and tags every activity with this brand for tracking.
							</p>
						)}
					</section>

					{/* Slack notification */}
					{slackBots.length > 0 && (
						<section className="space-y-3">
							<div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
								4 · Slack notification
							</div>
							<div className="space-y-1.5">
								<label className="text-xs text-muted-foreground">Notify on session start & completion</label>
								<Select value={slackBotId || 'none'} onValueChange={(v) => onSlackBotIdChange(v === 'none' ? '' : v)}>
									<SelectTrigger><SelectValue /></SelectTrigger>
									<SelectContent>
										<SelectItem value="none">No notification</SelectItem>
										{slackBots.filter(b => b.enabled).map(b => (
											<SelectItem key={b.id} value={b.id}>
												{b.name} {b.channel_name ? `(${b.channel_name})` : ''}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						</section>
					)}

					{/* Files */}
					{selectedIds.size > 0 && (
						<section className="space-y-3">
							<div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
								5 · Files (optional)
							</div>
							<FileUploadPanel syncedPadIds={Array.from(selectedIds)} devices={devices} />
						</section>
					)}

					{/* Start */}
					<div className="flex justify-end pt-2">
						<Button
							size="lg"
							onClick={onStart}
							disabled={selectedIds.size < 1 || (!task.trim() && !engagementActive) || isStarting}
						>
							{isStarting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
							Start agents ({selectedIds.size})
						</Button>
					</div>
				</>
			)}
		</div>
	);
};

// === Control Mode ===

const ControlMode = ({
	groupId,
	deviceNames,
	sessionKind,
	session,
	onStop,
	onBack,
}: {
	groupId: string;
	deviceNames: Record<string, string>;
	sessionKind: 'live' | 'persisted';
	session?: {
		agent_statuses?: Record<string, string>;
		device_step_counts?: Record<string, number>;
		max_steps?: number;
	} | null;
	onStop: () => void;
	onBack: () => void;
}) => {
	const [selectedPadId, setSelectedPadId] = useState<string | null>(null);
	const [deviceStates, setDeviceStates] = useState<Record<string, DeviceStreamState>>(() => {
		const cached = getCachedDeviceStates(groupId);
		if (cached) return cached;
		// Past (persisted) session with no live run — render its final statuses
		// instead of "running / 0" defaults.
		if (sessionKind === 'persisted' && session) {
			const seeded: Record<string, DeviceStreamState> = {};
			for (const padId of Object.keys(deviceNames)) {
				const status = session.agent_statuses?.[padId];
				seeded[padId] = {
					status: status === 'completed' || status === 'failed' || status === 'stopped'
						? status
						: 'running' as const,
					step: session.device_step_counts?.[padId] ?? 0,
					maxSteps: session.max_steps ?? 200,
					messages: [],
				};
			}
			return seeded;
		}
		return {};
	});

	const hasState = Object.keys(deviceStates).length > 0;
	const [isStreaming, setIsStreaming] = useState(sessionKind === 'live');
	const [streamEnded, setStreamEnded] = useState(sessionKind === 'persisted');

	// Connect to stream via group-stream-store (persists across tab switches)
	useEffect(() => {
		if (streamEnded) return;
		if (sessionKind === 'persisted') return;

		setIsStreaming(true);

		const unsubscribe = subscribeToGroupStream(
			groupId,
			Object.keys(deviceNames),
			(padId, stateUpdate) => {
				setDeviceStates(prev => {
					const current = prev[padId] || { status: 'running' as const, step: 0, maxSteps: 200, messages: [] };
					return {
						...prev,
						[padId]: {
							...current,
							...stateUpdate,
							messages: stateUpdate.messages
								? stateUpdate.messages
								: current.messages || [],
						},
					};
				});
			},
			() => {
				setIsStreaming(false);
				setStreamEnded(true);
			},
		);

		return () => {
			unsubscribe();
		};
	}, [groupId, deviceNames, streamEnded, sessionKind]);

	const handleStop = useCallback(() => {
		setIsStreaming(false);
		setStreamEnded(true);
		onStop();
	}, [onStop]);

	const padIds = Object.keys(deviceNames);
	const completedCount = padIds.filter(pid => {
		const s = deviceStates[pid]?.status;
		return s === 'completed' || s === 'failed' || s === 'stopped';
	}).length;

	return (
		<div className="flex flex-col h-full">
			{/* Cockpit sub-header */}
			<div className="flex items-center gap-3 px-6 py-3 border-b shrink-0">
				<Button variant="ghost" size="sm" onClick={onBack}>
					<ArrowLeft className="h-4 w-4 mr-1" /> Back
				</Button>
				<div className="flex items-baseline gap-3 flex-1 min-w-0">
					<span className="text-sm font-semibold truncate">Live run</span>
					<span className="text-xs text-muted-foreground font-mono tabular-nums">
						{padIds.length} {padIds.length === 1 ? 'device' : 'devices'}
					</span>
					<span className="text-xs text-muted-foreground font-mono tabular-nums">
						{completedCount}/{padIds.length} done
					</span>
				</div>
				{isStreaming && (
					<span className="inline-flex items-center gap-1.5 text-xs text-green-600">
						<span className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" />
						streaming
					</span>
				)}
				{isStreaming ? (
					<Button size="sm" variant="destructive" onClick={handleStop}>
						<Square className="h-3 w-3 mr-1" /> Stop all
					</Button>
				) : (
					<span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
						<span className="h-1.5 w-1.5 rounded-full bg-muted-foreground" />
						completed
					</span>
				)}
			</div>

			{/* Per-device cards grid */}
			<div className="flex-1 overflow-y-auto p-4">
				<div className={cn(
					"grid gap-4 auto-rows-[350px]",
					padIds.length <= 2 ? "grid-cols-1 md:grid-cols-2" :
					padIds.length <= 4 ? "grid-cols-1 md:grid-cols-2" :
					"grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
				)}>
					{!hasState ? (
						padIds.map((padId) => (
							<div
								key={padId}
								className="rounded-lg border bg-muted/30 flex flex-col items-center justify-center gap-2 text-muted-foreground"
							>
								<Loader2 className="h-6 w-6 animate-spin" />
								<span className="text-sm font-medium">
									{deviceNames[padId] || padId.slice(0, 8)}
								</span>
								<span className="text-xs">Loading state...</span>
							</div>
						))
					) : (
						padIds.map((padId) => (
							<DeviceCard
								key={padId}
								name={deviceNames[padId] || padId.slice(0, 8)}
								state={deviceStates[padId] || { status: 'running', step: 0, maxSteps: 200, messages: [] }}
								isSelected={selectedPadId === padId}
								onClick={() => setSelectedPadId(prev => prev === padId ? null : padId)}
							/>
						))
					)}
				</div>
			</div>

			{/* Device Detail Sheet */}
			{selectedPadId && deviceStates[selectedPadId] && (
				<DeviceDetailSheet
					padId={selectedPadId}
					deviceName={deviceNames[selectedPadId] || selectedPadId.slice(0, 8)}
					state={deviceStates[selectedPadId]}
					open={!!selectedPadId}
					onOpenChange={(open) => { if (!open) setSelectedPadId(null); }}
				/>
			)}
		</div>
	);
};

// === Main Page ===

export default function VMOSAgenticGroupPage() {
	const { groupId: urlGroupId } = useParams<{ groupId?: string }>();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [searchParams] = useSearchParams();
	const runId = searchParams.get('run');

	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const [task, setTask] = useState('');
	const [persona, setPersona] = useState('');
	const [brandId, setBrandId] = useState<string>('');  // '' = no brand
	const [maxSteps, setMaxSteps] = useState(200);
	const [slackBotId, setSlackBotId] = useState<string>('');  // '' = no notification
	const [disableProactiveSearch, setDisableProactiveSearch] = useState(false);
	const [returnHomeOnFinish, setReturnHomeOnFinish] = useState(true);
	const [captureRequired, setCaptureRequired] = useState(true);
	const [maxCommentsPerSession, setMaxCommentsPerSession] = useState<number | ''>('');
	const [maxSearchesPerSession, setMaxSearchesPerSession] = useState<number | ''>('');
	const [deviceNames, setDeviceNames] = useState<Record<string, string>>({});
	const [appliedTemplateId, setAppliedTemplateId] = useState<string | null>(null);
	const [engagement, setEngagement] = useState<string>('');  // '' = off (use task)
	const [platform, setPlatform] = useState<string>('');      // '' = auto per device
	const [runStatus, setRunStatus] = useState<Awaited<ReturnType<typeof getContentRunStatus>> | null>(null);

	// When launched from a multi-platform content upload (?run=<run_id>), poll
	// the run and auto-advance to the next platform's group as each finishes.
	// Stops polling once the run reaches a terminal state.
	const runStatusRef = useRef<Awaited<ReturnType<typeof getContentRunStatus>> | null>(null);
	useEffect(() => {
		if (!runId) return;
		let cancelled = false;
		let timer: ReturnType<typeof setInterval> | null = null;
		const terminalStates = new Set(['completed', 'completed_with_issues', 'failed', 'stopped']);

		const poll = async () => {
			try {
				const status = await getContentRunStatus(runId);
				if (cancelled) return;
				runStatusRef.current = status;
				setRunStatus(status);
				// Advance when the run moves to a group we aren't viewing yet.
				if (
					urlGroupId &&
					status.current_group_id &&
					status.status === 'running' &&
					status.current_group_id !== urlGroupId &&
					status.groups.length > 1
				) {
					navigate(`/runs/${status.current_group_id}?run=${runId}`, { replace: true });
					return;
				}
				if (terminalStates.has(status.status) && timer) {
					clearInterval(timer);
					timer = null;
				}
			} catch {
				// Run may not be created yet; keep polling until it is.
			}
		};
		poll();
		timer = setInterval(poll, 8000);
		return () => {
			cancelled = true;
			if (timer) clearInterval(timer);
		};
	}, [runId, urlGroupId, navigate]);

	const stopRun = useCallback(async () => {
		if (!runId) return;
		try {
			await stopContentRun(runId);
			toast.success('Multi-platform run stopped');
		} catch (e) {
			toast.error(e instanceof Error ? e.message : 'Failed to stop run');
		}
	}, [runId]);

	// Fetch devices (reuse existing endpoint)
	const { data: devicesResponse, isLoading } = useQuery({
		queryKey: ['vmos-devices'],
		queryFn: fetchDevices,
	});

	const devices = devicesResponse?.devices ?? [];

	const { data: slackBots = [] } = useQuery({
		queryKey: ['slack-bots'],
		queryFn: listSlackBots,
		staleTime: 60_000,
	});

	// Fetch session data when navigating to /:groupId
	const { data: sessionData, isLoading: sessionLoading, isError: sessionNotFound } = useQuery({
		queryKey: ['agentic-session', urlGroupId],
		queryFn: async () => {
			try {
				const data = await getGroupRunStatus(urlGroupId!);
				return { kind: 'live' as const, data };
			} catch {
				const data = await getSessionDetail(urlGroupId!);
				return { kind: 'persisted' as const, data };
			}
		},
		enabled: !!urlGroupId,
		retry: false,
	});

	// Hydrate deviceNames from session + devices query when loading from URL
	useEffect(() => {
		if (sessionData && devices.length > 0) {
			const names: Record<string, string> = {};
			for (const padId of sessionData.data.pad_ids) {
				const device = devices.find(d => d.pad_id === padId);
				names[padId] = device?.device_name || padId.slice(0, 8);
			}
			setDeviceNames(names);
		}
	}, [sessionData, devices]);

	// Reset deviceNames when navigating back to setup
	useEffect(() => {
		if (!urlGroupId) {
			setDeviceNames({});
		}
	}, [urlGroupId]);

	const handleToggle = useCallback((padId: string) => {
		setSelectedIds(prev => {
			const next = new Set(prev);
			if (next.has(padId)) {
				next.delete(padId);
			} else {
				next.add(padId);
			}
			return next;
		});
	}, []);

	const handleApplyTemplate = useCallback((t: PromptTemplate) => {
		setTask(t.task);
		setPersona(t.persona_text ?? '');
		setBrandId(t.brand_id ?? '');
		if (typeof t.max_steps === 'number') setMaxSteps(t.max_steps);
		setDisableProactiveSearch(!!t.disable_proactive_search);
		setCaptureRequired(t.capture_required ?? true);
		setReturnHomeOnFinish(t.return_home_on_finish ?? true);
		setMaxCommentsPerSession(t.max_comments_per_session ?? '');
		setMaxSearchesPerSession(t.max_searches_per_session ?? '');
	}, []);

	const handleApplyPersona = useCallback((p: Persona) => {
		setPersona(p.persona_text);
	}, []);

	const runFormValues: RunFormSnapshot = {
		task,
		persona,
		brandId,
		llmModel: DEFAULT_LLM_MODEL,
		commentLlmModel: DEFAULT_COMMENT_MODEL,
		maxSteps,
		disableProactiveSearch,
		captureRequired,
		returnHomeOnFinish,
		maxCommentsPerSession,
		maxSearchesPerSession,
	};

	// Preview per-device prompt/persona resolution for the chosen engagement.
	const previewQuery = useQuery({
		queryKey: ['run-preview', engagement, platform, Array.from(selectedIds).sort().join(',')],
		queryFn: () => previewGroupAgents(Array.from(selectedIds), engagement, platform || undefined),
		enabled: !!engagement && selectedIds.size > 0,
		retry: false,
	});

	// Start group agents
	const startMutation = useMutation({
		mutationFn: async () => {
			return startGroupAgents({
				pad_ids: Array.from(selectedIds),
				max_steps: maxSteps,
				llm_model: DEFAULT_LLM_MODEL,
				comment_llm_model: DEFAULT_COMMENT_MODEL,
				...(task.trim() && { task: toAscii(task.trim()) }),
				...(engagement && { engagement, ...(platform && { platform }) }),
				...(persona.trim() && { persona: toAscii(persona.trim()) }),
				...(brandId && { brand_id: brandId }),
				...(slackBotId && { slack_bot_id: slackBotId }),
				...(disableProactiveSearch && { disable_proactive_search: true }),
				...(!returnHomeOnFinish && { return_home_on_finish: false }),
				...(captureRequired === false && { capture_required: false }),
				...(maxCommentsPerSession !== '' && { max_comments_per_session: maxCommentsPerSession }),
				...(maxSearchesPerSession !== '' && { max_searches_per_session: maxSearchesPerSession }),
			});
		},
		onSuccess: (data) => {
			const names: Record<string, string> = {};
			for (const padId of data.pad_ids) {
				const device = devices.find(d => d.pad_id === padId);
				names[padId] = device?.device_name || padId.slice(0, 8);
			}
			setDeviceNames(names);
			navigate(`/runs/${data.group_id}`, { replace: true });
			queryClient.invalidateQueries({ queryKey: ['active-runs'] });
			queryClient.invalidateQueries({ queryKey: ['sessions-list'] });

			// Attribute this launch to the saved prompt it came from (fire-and-forget).
			if (appliedTemplateId) {
				recordTemplateUse(appliedTemplateId).catch(() => {});
			}

			if (Object.keys(data.failures).length > 0) {
				toast.warning(`Some agents failed to start: ${Object.values(data.failures).join(', ')}`);
			} else {
				toast.success(`Started agents on ${data.pad_ids.length} devices`);
			}
		},
		onError: (error) => {
			toast.error(error instanceof Error ? error.message : 'Failed to start group agents');
		},
	});

	// Stop group agents
	const stopMutation = useMutation({
		mutationFn: async () => {
			if (!urlGroupId) throw new Error('No group ID');
			return stopGroupAgents(urlGroupId);
		},
		onSuccess: () => {
			toast.success('Group agents stopped');
			queryClient.invalidateQueries({ queryKey: ['active-runs'] });
			queryClient.invalidateQueries({ queryKey: ['sessions-list'] });
		},
		onError: (error) => {
			toast.error(error instanceof Error ? error.message : 'Failed to stop agents');
		},
	});

	const handleBack = useCallback(() => {
		navigate('/runs');
	}, [navigate]);

	return (
		<AppShell>
			{/* Header */}
			<PageHeader>
					<SidebarTrigger className="data-[state=open]:hidden text-primary">
						<PanelLeftOpen className="h-4 w-4" />
					</SidebarTrigger>
					<div className="flex flex-col">
						<h1 className="text-lg font-semibold leading-tight">Runs</h1>
						<p className="text-xs text-muted-foreground">
							Operate AI agents across your connected devices.
						</p>
					</div>
				</PageHeader>

				{/* Content */}
				<div className="flex-1 min-h-0 overflow-y-auto">
					{isLoading || sessionLoading ? (
						<div className="flex items-center justify-center h-full">
							<Loader2 className="h-8 w-8 animate-spin text-primary" />
						</div>
					) : sessionNotFound && urlGroupId ? (
						<div className="flex items-center justify-center h-full px-6">
							<div className="max-w-md w-full flex flex-col items-center gap-3 py-12 text-center border rounded-lg">
								<AlertCircle className="h-8 w-8 text-muted-foreground/40" />
								<div className="space-y-1">
									<div className="text-sm font-medium">Session not found</div>
									<p className="text-xs text-muted-foreground">
										The session <code className="font-mono text-[11px] bg-muted px-1 py-0.5 rounded">{urlGroupId}</code> doesn't exist or has expired.
									</p>
								</div>
								<Button size="sm" onClick={handleBack}>
									<ArrowLeft className="h-4 w-4 mr-2" /> Back to setup
								</Button>
							</div>
						</div>
					) : urlGroupId && Object.keys(deviceNames).length > 0 ? (
						<div className="flex flex-col h-full">
							{runStatus && runStatus.groups.length > 1 && (
								<div className="px-6 py-2 border-b bg-muted/20 text-xs flex items-center gap-3 flex-wrap">
									<span className="font-medium text-muted-foreground">Multi-platform run:</span>
									<div className="flex items-center gap-1.5 flex-wrap">
										{runStatus.groups.map((g, i) => {
											const isCurrent = runStatus.current_index === i;
											const started = !!g.group_id;
											const done = runStatus.status === 'completed' || (started && i < runStatus.current_index);
											return (
												<span
													key={`${g.platform}-${i}`}
													className={cn(
														"px-2 py-0.5 rounded border text-[11px] font-medium capitalize",
														isCurrent
															? "bg-primary text-primary-foreground border-primary"
															: done
																? "bg-green-600/10 text-green-700 border-green-600/30"
																: "text-muted-foreground border-input",
													)}
												>
													{g.platform} · {g.pad_count}
												</span>
											);
										})}
									</div>
									{runStatus.status === 'running' && (
										<Button variant="outline" size="sm" className="h-6 text-xs ml-auto" onClick={stopRun}>
											Stop whole run
										</Button>
									)}
									{runStatus.status === 'completed' && (
										<span className="text-green-700 font-medium ml-auto">Completed</span>
									)}
								</div>
							)}
							<ControlMode
								groupId={urlGroupId}
								deviceNames={deviceNames}
								sessionKind={sessionData?.kind ?? 'live'}
								session={sessionData?.data ?? null}
								onStop={() => stopMutation.mutate()}
								onBack={handleBack}
							/>
						</div>
					) : !urlGroupId ? (
						<div className="px-6 py-6">
							<div className="max-w-4xl mb-4 rounded-lg border p-3 space-y-3">
								<div className="grid grid-cols-2 gap-3">
									<div className="space-y-1.5">
										<label className="text-xs text-muted-foreground">Engagement (auto-apply device prompts)</label>
										<Select value={engagement || 'off'} onValueChange={(v) => setEngagement(v === 'off' ? '' : v)}>
											<SelectTrigger><SelectValue /></SelectTrigger>
											<SelectContent>
												<SelectItem value="off">Off — use the task below</SelectItem>
												<SelectItem value="full">Full engagement</SelectItem>
												<SelectItem value="lurk">Lurk</SelectItem>
												<SelectItem value="recovery">Recovery</SelectItem>
												<SelectItem value="recovery_exploration">Recovery exploration</SelectItem>
											</SelectContent>
										</Select>
									</div>
									<div className="space-y-1.5">
										<label className="text-xs text-muted-foreground">Platform</label>
										<Select value={platform || 'auto'} onValueChange={(v) => setPlatform(v === 'auto' ? '' : v)}>
											<SelectTrigger><SelectValue /></SelectTrigger>
											<SelectContent>
												<SelectItem value="auto">Auto (per device)</SelectItem>
												<SelectItem value="tiktok">TikTok</SelectItem>
												<SelectItem value="instagram">Instagram</SelectItem>
												<SelectItem value="facebook">Facebook</SelectItem>
												<SelectItem value="youtube">YouTube Shorts</SelectItem>
											</SelectContent>
										</Select>
									</div>
								</div>
								{engagement && (
									<p className="text-[11px] text-muted-foreground">
										Each selected device runs its own saved prompt/persona. The Task/Persona below act as overrides.
									</p>
								)}
								{engagement && selectedIds.size > 0 && (
									<div className="text-xs">
										{previewQuery.isLoading ? (
											<span className="text-muted-foreground">Resolving per-device prompts…</span>
										) : previewQuery.isError ? (
											<span className="text-destructive">{(previewQuery.error as Error).message}</span>
										) : (
											<div className="space-y-1 max-h-40 overflow-y-auto">
												{previewQuery.data?.preview.map((p: RunPreviewEntry) => (
													<div key={p.pad_id} className="flex items-center gap-2">
														<span className="font-medium truncate w-40">{p.device_name}</span>
														<span className="text-muted-foreground truncate">{p.template_name}</span>
													</div>
												))}
											</div>
										)}
									</div>
								)}
							</div>
							<div className="max-w-4xl mb-6">
								<SavedPromptsPanel
									values={runFormValues}
									onApplyTemplate={handleApplyTemplate}
									onApplyPersona={handleApplyPersona}
									onAppliedTemplateChange={setAppliedTemplateId}
									selectedPadIds={Array.from(selectedIds)}
								/>
							</div>
							<SetupMode
								devices={devices}
								selectedIds={selectedIds}
								onToggle={handleToggle}
								task={task}
								onTaskChange={setTask}
								persona={persona}
								onPersonaChange={setPersona}
								brandId={brandId}
								onBrandIdChange={setBrandId}
								slackBotId={slackBotId}
								onSlackBotIdChange={setSlackBotId}
								slackBots={slackBots}
								maxSteps={maxSteps}
								onMaxStepsChange={setMaxSteps}
						disableProactiveSearch={disableProactiveSearch}
							onDisableProactiveSearchChange={setDisableProactiveSearch}
							returnHomeOnFinish={returnHomeOnFinish}
							onReturnHomeOnFinishChange={setReturnHomeOnFinish}
						captureRequired={captureRequired}
						onCaptureRequiredChange={setCaptureRequired}
						maxCommentsPerSession={maxCommentsPerSession}
						onMaxCommentsPerSessionChange={setMaxCommentsPerSession}
						maxSearchesPerSession={maxSearchesPerSession}
						onMaxSearchesPerSessionChange={setMaxSearchesPerSession}
						onStart={() => startMutation.mutate()}
								isStarting={startMutation.isPending}
								engagementActive={!!engagement}
							/>
						</div>
					) : null}
				</div>
		</AppShell>
	);
}
